import React from 'react';
import { useLocation } from 'react-router-dom';
import AnimatedFooter from './ui/animated-footer';
import AnalogClock from './ui/analog-clock';
import './Footer.css';

const Footer: React.FC = () => {
  const location = useLocation();

  const leftLinks = [
    { href: 'https://www.linkedin.com/in/aditya-naik-b2946a197/', label: 'LinkedIn', ariaLabel: 'LinkedIn' },
    { href: 'https://dribbble.com/adityamnaik', label: 'Dribbble', ariaLabel: 'Dribbble' },
    { href: 'https://github.com/Aditya-Naik13', label: 'Github', ariaLabel: 'Github' },
    { href: 'https://medium.com/@adityanaik13', label: 'Medium', ariaLabel: 'Medium' },
    { href: 'https://x.com/adityanaik0', label: 'X', ariaLabel: 'X (Twitter)' },
  ];

  const [showMonsterPopup, setShowMonsterPopup] = React.useState(false);
  const [showCalendlyPopup, setShowCalendlyPopup] = React.useState(false);
  const [preloadCalendly, setPreloadCalendly] = React.useState(false);
  const popupTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const calendlyTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (popupTimeoutRef.current) {
      clearTimeout(popupTimeoutRef.current);
    }
    setShowMonsterPopup(true);
  };

  const handleMouseLeave = () => {
    popupTimeoutRef.current = setTimeout(() => {
      setShowMonsterPopup(false);
    }, 300);
  };

  const handleCalendlyMouseEnter = () => {
    if (calendlyTimeoutRef.current) {
      clearTimeout(calendlyTimeoutRef.current);
    }
    // Start preloading the iframe immediately
    setPreloadCalendly(true);
    // Show popup after delay
    calendlyTimeoutRef.current = setTimeout(() => {
      setShowCalendlyPopup(true);
    }, 1000);
  };

  const handleCalendlyMouseLeave = () => {
    if (calendlyTimeoutRef.current) {
      clearTimeout(calendlyTimeoutRef.current);
    }
    calendlyTimeoutRef.current = setTimeout(() => {
      setShowCalendlyPopup(false);
    }, 300);
  };

  const pixelHeart = (
    <img
      src="/content/heart.png"
      alt="heart"
      className="pixel-heart-image"
      style={{ display: 'inline-block', height: '16px', width: 'auto', verticalAlign: 'middle' }}
    />
  );

  // Handle scroll to top
  const handleScrollToTop = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filter navigation links based on current page
  const allRightLinks = [
    { href: '#top', label: '↑ Back to top', onClick: handleScrollToTop },
    { href: '/', label: 'Home', showOnPath: '/about' }, // Only show on about page
    { href: '#projects', label: 'Projects', showOnPath: '/' }, // Only show on home page
    { href: '/content/AdityaNaik-Resume .pdf', label: 'Resume' },
  ];

  const rightLinks = allRightLinks.filter(link => {
    // Always show "Back to top" and "Resume"
    if (!link.showOnPath) return true;
    // Show link only if we're on the specified path
    return location.pathname === link.showOnPath;
  });

  return (
    <AnimatedFooter
      leftLinks={leftLinks}
      rightLinks={rightLinks}
      copyrightText={
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span>© {new Date().getFullYear()} Aditya Naik.</span>
          <a
            href="https://creativecommons.org/licenses/by-nc-nd/4.0/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              textDecoration: 'none',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            aria-label="Licensed under Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International"
          >
            <img
              src="https://mirrors.creativecommons.org/presskit/buttons/88x31/svg/by-nc-nd.svg"
              alt="CC BY-NC-ND 4.0"
              style={{ height: '16px', width: 'auto' }}
            />
          </a>
        </span>
      }
      leftChildren={
        <>
          <div className="footer-cta-section">
            <div className="footer-cta-text-container">
              <span className="footer-cta-title">Don't be a stranger, say hi ^^</span>
              <span className="footer-cta-subtitle">Work, camera geek talk, or just anything <em>I'm all ears</em></span>
            </div>
            <div className="footer-cta-buttons">
              <a
                href="mailto:adityamanojnaik@gmail.com"
                className="footer-cta-button footer-cta-button-email"
                aria-label="Send me an email"
              >
                <span className="button-text-default">Email me</span>
                <span className="button-text-hover">adityamanojnaik@gmail.com</span>
              </a>
              <span
                className="calendly-button-wrapper"
                onMouseEnter={handleCalendlyMouseEnter}
                onMouseLeave={handleCalendlyMouseLeave}
              >
                <a
                  href="https://calendly.com/adityamanojnaik/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-cta-button footer-cta-button-calendly"
                  aria-label="Schedule a meeting"
                >
                  <span className="button-text-default">Schedule a meet</span>
                  <span className="button-text-hover">Open Calendly</span>
                </a>
                {preloadCalendly && (
                  <div className={`calendly-popup ${showCalendlyPopup ? 'visible' : 'hidden'}`}>
                    <iframe
                      src="https://calendly.com/adityamanojnaik/30min?embed_domain=localhost&embed_type=Inline"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      title="Schedule a meeting with Aditya"
                    />
                  </div>
                )}
              </span>
            </div>
          </div>
          {/* Temporarily disabled - will enable when showcased on these platforms
          <div className="footer-showcase-section">
            <div className="footer-showcase-text">Featured/Showcased on</div>
            <div className="footer-showcase-logos">
              <a href="https://www.awwwards.com/" target="_blank" rel="noopener noreferrer" aria-label="Awwwards">
                <img src="/content/showcase-logos/awwwards.svg" alt="Awwwards" className="showcase-logo" />
              </a>
              <a href="https://www.bestfolios.com/" target="_blank" rel="noopener noreferrer" aria-label="Bestfolios">
                <img src="/content/showcase-logos/bestfolios.svg" alt="Bestfolios" className="showcase-logo" />
              </a>
              <a href="https://www.killerportfolio.com/" target="_blank" rel="noopener noreferrer" aria-label="Killer Portfolio">
                <img src="/content/showcase-logos/killerportfolio.svg" alt="Killer Portfolio" className="showcase-logo" />
              </a>
              <a href="https://minimal.gallery/" target="_blank" rel="noopener noreferrer" aria-label="Minimal Gallery">
                <img src="/content/showcase-logos/minimal-gallery.svg" alt="Minimal Gallery" className="showcase-logo" />
              </a>
              <a href="https://uxfol.io/showcase" target="_blank" rel="noopener noreferrer" aria-label="UXfolio Showcase">
                <img src="/content/showcase-logos/uxfolio.svg" alt="UXfolio" className="showcase-logo" />
              </a>
              <a href="https://www.wallofportfolios.in/" target="_blank" rel="noopener noreferrer" aria-label="Wall of Portfolios">
                <img src="/content/showcase-logos/wall-of-portfolios.svg" alt="Wall of Portfolios" className="showcase-logo" />
              </a>
            </div>
          </div>
          */}
          <div className="footer-made-with-wrapper">
            <div className="footer-made-with">
              Made with {pixelHeart} and{' '}
              <span
                className="monster-ultra-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <span className="monster-ultra-link">
                  Monster
                </span>
                {showMonsterPopup && (
                  <div className="monster-popup">
                    <a
                      href="https://www.amazon.com/s?k=Monster+Energy+Zero+Ultra"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="monster-popup-link"
                    >
                      <img
                        src="/content/monster.jpg"
                        alt="Monster Ultra on Amazon"
                        className="monster-popup-image"
                      />
                      <div className="monster-popup-text">
                        <div className="monster-popup-title">Monster Energy Zero Ultra</div>
                        <div className="monster-popup-price">View on Amazon →</div>
                      </div>
                    </a>
                  </div>
                )}
              </span>
            </div>
            <div className="footer-disclaimer">
              I am not sponsored by them, but if your reading this, hit me up. Your boy needs some of that juice <span style={{ fontStyle: 'normal' }}>🥲</span>
            </div>
          </div>
        </>
      }
      rightChildren={
        <div style={{ marginRight: '16px' }}>
          <AnalogClock timezone="America/New_York" size={80} showTimezone={true} />
        </div>
      }
    />
  );
}

export default Footer;